import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";

import DrugScheduleService from "../../service/DrugScheduleService";
import {
  normaliseStatus, formatDateShort, drugNameOf, doseDisplayStatus,
} from "./helpers";

/**
 * Drug schedule — read-only View, opened via the "View schedule" link
 * on the list table. Renders as a centered modal (not a rail sheet),
 * so it's reachable from any status (Active / Completed / Expired)
 * without opening the editor column.
 *
 * The drug catalog is fetched here on mount so the criticalinstruction
 * can be rendered as sanitised HTML. The list is small (~15 drugs in
 * prod) and cached in memory only for the lifetime of this modal —
 * keeping the fetch local means the modal is self-contained and
 * DrugSchedule.js doesn't need to plumb it in.
 */

/** Render a value, or a MUTED em-dash when there is nothing to show
 *  (Figma 4473:15741 — the empty dose cells are a lighter grey than a
 *  real value, which a bare text node cannot express).
 *
 *  `formatDateShort` already answers "—" for unusable input, so that is
 *  treated as absent too: otherwise a bad date slips through the happy
 *  branch and renders an unmuted dash. */
const orDash = (value) =>
  !value || value === "\u2014" ? <span className="ds-dash">&mdash;</span> : value;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M6 18L18 6" />
  </svg>
);

const InstructionPanel = ({ html }) => {
  const text = String(html || "").trim();
  if (!text) return null;
  const clean = DOMPurify.sanitize(text, { USE_PROFILES: { html: true } });
  return (
    <div className="ds-instr">
      <div className="ds-instr-head">
        <span className="ds-instr-label">Instruction for planner</span>
      </div>
      <div
        className="ds-instr-body"
        // Content is sanitised via dompurify above; the drug catalog's
        // criticalinstruction carries <b> tags for the important bits.
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </div>
  );
};
InstructionPanel.propTypes = { html: PropTypes.string };

const DrugScheduleView = ({ plan, onClose }) => {
  const service = useRef(new DrugScheduleService()).current;
  const [criticalInstruction, setCriticalInstruction] = useState("");

  useEffect(() => {
    let cancelled = false;
    const drugName = drugNameOf(plan).toLowerCase();
    if (!drugName) return undefined;
    service.listDrugs(
      (list) => {
        if (cancelled) return;
        const hit = (list || []).find(
          (d) => String(d.drugname || "").toLowerCase() === drugName,
        );
        setCriticalInstruction((hit && hit.criticalinstruction) || "");
      },
      () => { if (!cancelled) setCriticalInstruction(""); },
    );
    return () => { cancelled = true; };
  }, [plan, service]);

  const status = normaliseStatus(plan.status) || "";

  return (
    <div className="ds-viewmodal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ds-viewmodal"
        role="dialog"
        aria-modal="true"
        aria-label={`Drug schedule for ${drugNameOf(plan) || "this drug"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ds-viewmodal-head">
          <div className="ds-viewmodal-titles">
            <h3 className="ds-sheet-title">{drugNameOf(plan) || "Schedule"}</h3>
            <p className="ds-sheet-sub">
              Created {formatDateShort((plan.createdAt || "").slice(0, 10))}
              {" · by "}{orDash(plan.createdby)}
            </p>
          </div>
          <div className="ds-viewmodal-actions">
            {status ? (
              <span className={`ds-chip ds-chip-static is-${status.toLowerCase()}`}>
                <span className="ds-chip-dot" />{status}
              </span>
            ) : null}
            <button
              type="button"
              className="ds-sheet-close"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        <div className="ds-viewmodal-body">
          <div className="ds-view-table" role="table">
            <div className="ds-view-thead" role="row">
              <span role="columnheader" className="ds-view-cell ds-vc-no">DOSE</span>
              <span role="columnheader" className="ds-view-cell ds-vc-planned">PLANNED</span>
              <span role="columnheader" className="ds-view-cell ds-vc-actual">ACTUAL</span>
              <span role="columnheader" className="ds-view-cell ds-vc-status">DOSE STATUS</span>
            </div>
            {(plan.doses || []).map((d, i) => {
              const state = doseDisplayStatus(d, status);
              return (
                <div className="ds-view-row" role="row" key={i}>
                  <span role="cell" className="ds-view-cell ds-vc-no">{d.doseno || i + 1}</span>
                  <span role="cell" className="ds-view-cell ds-vc-planned">{orDash(formatDateShort(d.planneddate))}</span>
                  <span role="cell" className="ds-view-cell ds-vc-actual">
                    {orDash(d.actualdate ? formatDateShort(d.actualdate) : "")}
                  </span>
                  <span role="cell" className="ds-view-cell ds-vc-status">
                    <span className={`ds-dose-chip is-${state.toLowerCase()}`}>
                      <span className="ds-chip-dot" />{state}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          <InstructionPanel html={criticalInstruction} />
        </div>
      </div>
    </div>
  );
};

DrugScheduleView.propTypes = {
  plan:    PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DrugScheduleView;
